/**
 * Form primitives for the member checkout screens.
 *
 * These deliberately don't reuse the in-app shadcn inputs: checkout is a public,
 * WorldVia-branded page with its own pill-shaped light theme. Everything here is
 * label-associated and wires `aria-invalid` / `aria-describedby` for errors.
 */

import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { AlertCircle, Check, X } from 'lucide-react';

export const fieldBoxClass = (invalid?: boolean) =>
  [
    'flex h-11 w-full items-center rounded-full border bg-white px-4 text-[13.5px] text-[#3d1751]',
    'placeholder:text-[#a58fb7] focus:outline-none focus:ring-2 focus:ring-[#b82f74] focus:ring-offset-1',
    invalid ? 'border-[1.5px] border-[#dc2626] bg-[#fffbfb]' : 'border-[#d9d3e4]',
  ].join(' ');

export const buttonClass = (
  variant: 'primary' | 'outline' | 'ghost' | 'sm' | 'smOutline' | 'smGhost',
) => {
  const base =
    'rounded-full font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#b82f74] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';
  const variants: Record<typeof variant, string> = {
    primary: 'flex-1 bg-[#b82f74] px-4 py-3 text-[13.5px] text-white hover:bg-[#9d2762]',
    outline:
      'flex-1 border border-[#d9d3e4] bg-white px-4 py-3 text-[13.5px] text-[#3d1751] hover:bg-[#faf7fb]',
    ghost: 'flex-1 px-4 py-3 text-[13.5px] text-[#7a6a8c] hover:text-[#3d1751]',
    sm: 'bg-[#b82f74] px-4 py-2 text-[12.5px] text-white hover:bg-[#9d2762]',
    smOutline:
      'border border-[#d9d3e4] bg-white px-4 py-2 text-[12.5px] text-[#3d1751] hover:bg-[#faf7fb]',
    smGhost: 'px-3 py-2 text-[12.5px] text-[#7a6a8c] underline hover:text-[#3d1751]',
  };
  return `${base} ${variants[variant]}`;
};

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}

/** Label + hint + error scaffolding shared by every checkout input. */
export function Field({
  label,
  htmlFor,
  required,
  optional,
  hint,
  error,
  children,
  className = '',
}: FieldProps) {
  return (
    <div className={`mb-4 ${className}`}>
      <div className="mb-1 flex items-baseline gap-1.5">
        <label htmlFor={htmlFor} className="text-xs font-semibold text-[#3d1751]">
          {label}
        </label>
        {required ? (
          <span className="text-xs text-[#ff7540]" aria-hidden="true">
            *
          </span>
        ) : null}
        {optional ? <span className="text-[11px] font-normal text-[#7a6a8c]">(optional)</span> : null}
      </div>
      {hint ? (
        <p id={`${htmlFor}-hint`} className="mb-1.5 text-[11px] leading-snug text-[#7a6a8c]">
          {hint}
        </p>
      ) : null}
      {children}
      {error ? (
        <p
          id={`${htmlFor}-error`}
          className="mt-1.5 flex items-center gap-1 text-[11.5px] font-medium text-[#dc2626]"
        >
          <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { id, label, hint, error, optional, required, className, ...inputProps },
  ref,
) {
  return (
    <Field
      label={label}
      htmlFor={id}
      required={required}
      optional={optional}
      hint={hint}
      error={error}
      className={className}
    >
      <input
        {...inputProps}
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') ||
          undefined
        }
        className={fieldBoxClass(Boolean(error))}
      />
    </Field>
  );
});

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export function SelectField({
  id,
  label,
  error,
  hint,
  options,
  placeholder = 'Select…',
  required,
  className,
  ...selectProps
}: SelectFieldProps) {
  return (
    <Field
      label={label}
      htmlFor={id}
      required={required}
      hint={hint}
      error={error}
      className={className}
    >
      <select
        {...selectProps}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') ||
          undefined
        }
        className={`${fieldBoxClass(Boolean(error))} appearance-none bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23a58fb7'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E")] bg-[length:14px] bg-[right_1rem_center] bg-no-repeat pr-9`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

interface CheckRowProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  invalid?: boolean;
  describedBy?: string;
}

/** Bordered checkbox row used for "same as billing", terms and marketing opt-in. */
export function CheckRow({ id, checked, onChange, children, invalid, describedBy }: CheckRowProps) {
  return (
    <div
      className={`mb-3 flex items-start gap-3 rounded-xl border bg-[#fbfafc] p-3 ${
        invalid ? 'border-[1.5px] border-[#dc2626]' : 'border-[#ece6f0]'
      }`}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        className="mt-0.5 h-5 w-5 flex-none rounded-md border-[1.5px] border-[#cdbfda] accent-[#ff7540] focus:outline-none focus:ring-2 focus:ring-[#b82f74] focus:ring-offset-1"
      />
      <label htmlFor={id} className="text-[12.5px] leading-relaxed text-[#4a3a5c]">
        {children}
      </label>
    </div>
  );
}

interface ChoiceGroupProps {
  legend: string;
  name: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  required?: boolean;
  note?: string;
  error?: string;
  help?: ReactNode;
  className?: string;
}

/** Pill radio group — "Do you have associate advisors?" and the Accelerator format. */
export function ChoiceGroup({
  legend,
  name,
  value,
  options,
  onChange,
  required,
  note,
  error,
  help,
  className = '',
}: ChoiceGroupProps) {
  return (
    <fieldset className={`mb-4 ${className}`} aria-describedby={error ? `${name}-error` : undefined}>
      <legend className="mb-1 text-xs font-semibold text-[#3d1751]">
        {legend}
        {required ? (
          <span className="ml-1 text-[#ff7540]" aria-hidden="true">
            *
          </span>
        ) : null}
      </legend>
      {note ? <p className="mb-2 text-[10px] tracking-wide text-[#7a6a8c]">{note}</p> : null}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.value === value;
          const id = `${name}-${option.value}`;
          return (
            <div key={option.value} className="relative">
              <input
                type="radio"
                id={id}
                name={name}
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                className="peer absolute h-px w-px opacity-0"
              />
              <label
                htmlFor={id}
                className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-5 py-2 text-[12.5px] font-medium peer-focus-visible:ring-2 peer-focus-visible:ring-[#b82f74] peer-focus-visible:ring-offset-2 ${
                  selected
                    ? 'border-[#ff7540] bg-[#fff4ee] font-semibold text-[#c2410c]'
                    : 'border-[#d9d3e4] bg-white text-[#5c4a6e] hover:bg-[#faf7fb]'
                }`}
              >
                {selected ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : null}
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {help ? <div className="mt-2 text-[11.5px]">{help}</div> : null}
      {error ? (
        <p
          id={`${name}-error`}
          className="mt-1.5 flex items-center gap-1 text-[11.5px] font-medium text-[#dc2626]"
        >
          <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

interface PasswordRulesProps {
  id: string;
  rules: Array<{ id: string; label: string; met: boolean }>;
}

export function PasswordRules({ id, rules }: PasswordRulesProps) {
  return (
    <ul id={id} className="mt-2 flex flex-col gap-1.5">
      {rules.map((rule) => (
        <li key={rule.id} className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`flex h-4 w-4 flex-none items-center justify-center rounded-full text-white ${
              rule.met ? 'bg-[#02b899]' : 'bg-[#dc2626]'
            }`}
          >
            {rule.met ? <Check className="h-2.5 w-2.5" strokeWidth={4} /> : <X className="h-2.5 w-2.5" strokeWidth={4} />}
          </span>
          <span className={`text-[11.5px] ${rule.met ? 'text-[#0f7a63]' : 'text-[#dc2626]'}`}>
            <span className="sr-only">{rule.met ? 'Met: ' : 'Not met: '}</span>
            {rule.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Section divider inside the form column ("Billing address" / "Mailing address"). */
export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-3 mt-6 border-t border-[#f2eef5] pt-4 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[#7a6a8c]">
      {children}
    </h3>
  );
}
